// LeadListIndicator — small amber dot/chip surfacing on every render
// of a party who currently sits on mi.lead_list_items in a non-terminal
// status ('pending' or 'researched'). Once they're pushed, the green
// PushedIndicator takes over (lead_list_status flips to 'pushed' and
// last_pushed_to_close_at gets stamped — so we expose the pushed state
// only via PushedIndicator to avoid two competing badges).
//
// Variants mirror PushedIndicator so the two can sit side-by-side
// without surface drift.

export type LeadListIndicatorStatus = 'pending' | 'researched';

interface LeadListIndicatorProps {
  status: LeadListIndicatorStatus | null;
  variant?: 'inline' | 'badge';
  className?: string;
}

export function LeadListIndicator({ status, variant = 'inline', className = '' }: LeadListIndicatorProps) {
  if (!status) return null;

  const tooltip = status === 'researched'
    ? 'On lead list · researched (skip-trace done, ready to push)'
    : 'On lead list · pending';

  if (variant === 'badge') {
    return (
      <span
        title={tooltip}
        className={
          'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border border-amber/40 bg-amber/10 text-amber ' +
          className
        }
      >
        <span className="text-amber">●</span>
        {status === 'researched' ? 'on list · researched' : 'on list'}
      </span>
    );
  }

  // Inline: small amber dot. Researched gets a filled square so dense
  // rows can distinguish the two non-terminal states at a glance.
  return (
    <span
      title={tooltip}
      className={'inline-block text-amber text-[11px] leading-none align-middle ' + className}
      aria-label={tooltip}
    >
      {status === 'researched' ? '◆' : '●'}
    </span>
  );
}
